import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Container,
} from '@mui/material';
import {
  Google as GoogleIcon,
  Facebook as FacebookIcon,
  GitHub as GitHubIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const Login: React.FC = () => {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check for OAuth errors in URL parameters
    const urlParams = new URLSearchParams(location.search);
    const errorParam = urlParams.get('error');
    
    if (errorParam) {
      switch (errorParam) {
        case 'oauth_failed':
          setError('OAuth authentication failed. Please try again.');
          break;
        case 'token_invalid':
          setError('Invalid authentication token. Please login again.');
          break;
        case 'no_token':
          setError('No authentication token received. Please try again.');
          break;
        default:
          setError('An error occurred during login. Please try again.');
      }
      
      // Clear the error from URL
      window.history.replaceState({}, document.title, '/loginPage');
    }
  }, [location.search]);

  const handleLogin = (provider: string) => {
    try {
      setError(null);
      login(provider);
      // Note: login() now redirects immediately, so we don't need to navigate here
    } catch (err) {
      setError(`Failed to login with ${provider}. Please try again.`);
    }
  };

  const providers = [
    {
      name: 'Google',
      icon: <GoogleIcon />,
      color: '#4285f4',
      provider: 'google',
    },
    /*{
      name: 'Facebook',
      icon: <FacebookIcon />,
      color: '#1877f2',
      provider: 'facebook',
    },
    {
      name: 'GitHub',
      icon: <GitHubIcon />,
      color: '#333',
      provider: 'github',
    },
    {
      name: 'Email',
      icon: <EmailIcon />,
      color: '#666',
      provider: 'email',
    },*/
  ];

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            mb: 4,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <img
            src="/sonetch_logo.png"
            alt="SonEtch logo"
            style={{
              maxWidth: '200px',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </Box>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 400,
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" gutterBottom>
            Welcome Back
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
            Sign in to access your job management dashboard
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {providers.map((provider) => (
              <Button
                key={provider.provider}
                variant="outlined"
                size="large"
                startIcon={provider.icon}
                onClick={() => handleLogin(provider.provider)}
                disabled={loading}
                sx={{
                  justifyContent: 'flex-start',
                  borderColor: provider.color,
                  color: provider.color,
                  '&:hover': {
                    borderColor: provider.color,
                    backgroundColor: `${provider.color}10`,
                  },
                }}
              >
                Continue with {provider.name}
              </Button>
            ))}
          </Box>

          <Divider sx={{ my: 3 }}>
            
          </Divider>

          <Typography variant="body2" color="textSecondary">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </Typography>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 